import { EntityManager } from '../../../engine/ecs/core/EntityManager';
import { ComponentManager } from '../../../engine/ecs/core/ComponentManager';
import { Entity } from '../../../engine/ecs/core/Entity';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import { MovementComponent } from '../../../engine/ecs/components/MovementComponent';
import {
  SpriteComponent,
  RenderingType,
} from '../../../engine/ecs/components/SpriteComponent';
import {
  CollisionComponent,
  CollisionShape,
} from '../../../engine/ecs/components/CollisionComponent';
import {
  AnimationComponent,
  AnimationState,
} from '../../../engine/ecs/components/AnimationComponent';
import { PacmanComponent } from '../components/PacmanComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';

/**
 * PacmanEntityFactory - Creates Pacman player entities
 */
export class PacmanEntityFactory {
  private entityManager: EntityManager;
  private componentManager: ComponentManager;

  constructor() {
    this.entityManager = EntityManager.getInstance();
    this.componentManager = ComponentManager.getInstance();
  }

  /**
   * Create a Pacman entity at the specified position
   */
  public createPacman(x: number, y: number, speed?: number): Entity {
    const entity = this.entityManager.createEntity();

    // Use provided speed or default to 130
    const pacmanSpeed = speed || 130;

    // Transform component - position and orientation
    const transform = new TransformComponent(
      new Vector2(x, y), // position
      0, // rotation
      new Vector2(1, 1) // scale
    );
    this.componentManager.addComponent(entity, transform);

    // Movement component - for physics
    const movement = new MovementComponent(
      pacmanSpeed, // Use level-specific speed
      0.9, // drag
      new Vector2(0, 0), // initial velocity
      new Vector2(0, 0) // initial acceleration
    );
    this.componentManager.addComponent(entity, movement);

    // Sprite component - visual representation
    const sprite = new SpriteComponent(
      '#FFFF00', // yellow color
      new Vector2(22, 22), // size that fits well in a 24px cell
      undefined, // no texture for now
      true, // visible
      1.0, // opacity
      2, // z-index (above maze)
      RenderingType.TopDownPerson // Use top-down person rendering
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component - for collision detection
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(20, 20), // collision size slightly smaller than sprite
      new Vector2(0, 0), // offset
      false, // not a trigger
      'pacman', // collision layer
      ['wall', 'ghost', 'collectible'] // what can collide with
    );
    this.componentManager.addComponent(entity, collision);

    // Animation component - for sprite animations
    const animation = new AnimationComponent(
      AnimationState.Idle, // initial state
      0.15, // animation speed
      true // loop animations
    );
    // Remove texture references to use built-in shape rendering
    animation.addAnimation(AnimationState.Idle, []);
    animation.addAnimation(AnimationState.Chomping, []);
    this.componentManager.addComponent(entity, animation);

    // Pacman-specific component
    const pacmanComponent = new PacmanComponent(pacmanSpeed); // Use level-specific speed
    this.componentManager.addComponent(entity, pacmanComponent);

    return entity;
  }
}
